// src/client/components/admin/SchemaMigrationSection.tsx
import { RefreshCw, GitBranch, CheckCircle, AlertTriangle, Database } from 'lucide-react';
import type { SchemaDiffResponse, MigrationResult, SchemaDiff, SchemaDiffSummary } from '@shared/types/admin/migration.types';

// ============================================================================
// Component Props
// ============================================================================

interface SchemaMigrationSectionProps {
  onCheckSchema: () => void;
  onExecuteMigration: () => void;
  isChecking: boolean;
  isExecuting: boolean;
  diffData: SchemaDiffResponse | null;
  migrationResult: MigrationResult | null;
}

interface SchemaDiffViewProps {
  diff: SchemaDiff;
  summary: SchemaDiffSummary;
}

// ============================================================================
// Schema Diff View Component
// ============================================================================

function SchemaDiffView({ diff, summary }: SchemaDiffViewProps) {
  if (!summary.requiresMigration) {
    return (
      <div className="bg-green-50 border border-green-200 rounded p-4 flex items-center gap-2">
        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
        <span className="text-green-800">Schema ist aktuell - keine Migration erforderlich</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Missing Tables */}
      {diff.missingTables.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
          <h4 className="font-medium text-yellow-900 mb-2 flex items-center gap-2">
            <Database className="h-4 w-4" />
            Fehlende Tabellen ({diff.missingTables.length})
          </h4>
          <ul className="list-disc list-inside text-sm text-yellow-800 space-y-1">
            {diff.missingTables.map(table => (
              <li key={table} className="font-mono">{table}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Missing Columns */}
      {diff.missingColumns.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded p-4">
          <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
            <GitBranch className="h-4 w-4" />
            Fehlende Spalten ({diff.missingColumns.length})
          </h4>
          <ul className="list-disc list-inside text-sm text-blue-800 space-y-1">
            {diff.missingColumns.map((col, idx) => (
              <li key={idx}>
                <span className="font-mono">{col.tableName}.{col.columnName}</span>
                <span className="text-blue-600"> ({col.columnDef.type})</span>
                {col.columnDef.defaultValue !== undefined && (
                  <span className="text-blue-500 text-xs"> DEFAULT: {String(col.columnDef.defaultValue)}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Missing Indexes */}
      {diff.missingIndexes.length > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded p-4">
          <h4 className="font-medium text-purple-900 mb-2">
            Fehlende Indexes ({diff.missingIndexes.length})
          </h4>
          <ul className="list-disc list-inside text-sm text-purple-800 space-y-1">
            {diff.missingIndexes.map((idx, i) => (
              <li key={i}>
                <span className="font-mono">{idx.indexName}</span>
                <span className="text-purple-600"> on {idx.tableName}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Extra Columns (Warning) */}
      {diff.extraColumns.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded p-4">
          <h4 className="font-medium text-orange-900 mb-2 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Extra Spalten in DB ({diff.extraColumns.length})
          </h4>
          <p className="text-sm text-orange-800 mb-2">
            Diese Spalten existieren in der Datenbank, aber nicht in den Schema-Definitionen:
          </p>
          <ul className="list-disc list-inside text-sm text-orange-800 space-y-1">
            {diff.extraColumns.map((col, idx) => (
              <li key={idx} className="font-mono">
                {col.tableName}.{col.columnName}
              </li>
            ))}
          </ul>
          <p className="text-xs text-orange-700 mt-2 italic">
            ⚠️ Diese Spalten werden nicht automatisch entfernt (nur Warnung)
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Schema Migration Section Component
// ============================================================================

export default function SchemaMigrationSection({
  onCheckSchema,
  onExecuteMigration,
  isChecking,
  isExecuting,
  diffData,
  migrationResult
}: SchemaMigrationSectionProps) {
  return (
    <div className="bg-white rounded-lg shadow border">
      {/* Header */}
      <div className="px-4 py-3 border-b bg-gray-50">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <GitBranch className="h-4 w-4" />
          Schema Migration
        </h3>
      </div>

      <div className="p-4 space-y-4">
        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            onClick={onCheckSchema}
            disabled={isChecking}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {isChecking ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Prüfe Schema...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                Schema prüfen
              </>
            )}
          </button>

          {diffData?.summary.requiresMigration && (
            <button
              onClick={onExecuteMigration}
              disabled={isExecuting}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {isExecuting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Migration läuft...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Migration ausführen
                </>
              )}
            </button>
          )}
        </div>

        {/* Migration Result */}
        {migrationResult && (
          <div className={`p-4 rounded border ${
            migrationResult.success 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            <h4 className={`font-medium mb-2 flex items-center gap-2 ${
              migrationResult.success ? 'text-green-900' : 'text-red-900'
            }`}>
              {migrationResult.success ? (
                <>
                  <CheckCircle className="h-5 w-5" />
                  Migration erfolgreich
                </>
              ) : (
                <>
                  <AlertTriangle className="h-5 w-5" />
                  Migration mit Fehlern
                </>
              )}
            </h4>
            
            {migrationResult.operations.length > 0 && (
              <div className="mb-2">
                <p className={`text-sm font-medium mb-1 ${
                  migrationResult.success ? 'text-green-800' : 'text-red-800'
                }`}>
                  Durchgeführte Operationen:
                </p>
                <ul className={`list-disc list-inside text-sm ${
                  migrationResult.success ? 'text-green-700' : 'text-red-700'
                }`}>
                  {migrationResult.operations.map((op, idx) => (
                    <li key={idx}>{op}</li>
                  ))}
                </ul>
              </div>
            )}

            {migrationResult.errors && migrationResult.errors.length > 0 && (
              <div>
                <p className="text-sm font-medium text-red-800 mb-1">Fehler:</p>
                <ul className="list-disc list-inside text-sm text-red-700">
                  {migrationResult.errors.map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-xs text-gray-500 mt-2">
              {migrationResult.executedBy && `Ausgeführt von: ${migrationResult.executedBy} • `}
              {new Date(migrationResult.timestamp).toLocaleString('de-DE')}
            </p>
          </div>
        )}

        {/* Schema Diff Display */}
        {diffData && <SchemaDiffView diff={diffData.diff} summary={diffData.summary} />}
      </div>
    </div>
  );
}